import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Visibility as ViewCountIcon,
  GetApp as DownloadIcon,
  Pageview as ViewVersionIcon,
  SwapVert as ChangeStatusIcon,
  Star as MakeActiveIcon,
} from '@mui/icons-material';
import type { ContentVersion, VersionStatus } from '../../types/content.types';
import { contentService } from '../../services';
import CreateVersionDialog from './CreateVersionDialog';

interface VersionHistoryPanelProps {
  open: boolean;
  onClose: () => void;
  contentId: string;
  organizationId: string;
  teamId: string;
  currentVersionNumber: number;
  canPublish: boolean;
  onVersionPublished?: () => void;
  contentTitle?: string;
  contentMimeType?: string;
  /** The version currently being displayed in the detail page (null = live/current version) */
  selectedVersionNumber?: number | null;
  /** Called when the user wants to view a specific version (null = return to current) */
  onVersionSelect?: (versionNumber: number | null) => void;
}

/** Version status → chip display config (mirrors Content.status values) */
const VERSION_STATUS_CONFIG: Record<VersionStatus, { label: string; color: 'default' | 'success' | 'info' | 'warning' | 'error' }> = {
  public:   { label: 'Public',   color: 'success' },
  private:  { label: 'Private',  color: 'info' },
  draft:    { label: 'Draft',    color: 'default' },
  archived: { label: 'Archived', color: 'warning' },
};

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  open,
  onClose,
  contentId,
  organizationId,
  teamId,
  currentVersionNumber,
  canPublish,
  onVersionPublished,
  contentTitle,
  contentMimeType,
  selectedVersionNumber,
  onVersionSelect,
}) => {
  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVersion, setSelectedVersion] = useState<ContentVersion | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [makeActiveDialogOpen, setMakeActiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<VersionStatus>('draft');

  const loadVersions = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const result = await contentService.listVersions(contentId, canPublish, undefined, teamId);
      const list = result?.versions || result?.versionsList || result || [];
      setVersions(list);
    } catch (err: any) {
      setError(err?.message || 'Failed to load version history.');
    } finally {
      setLoading(false);
    }
  }, [open, contentId, canPublish, teamId]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, version: ContentVersion) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedVersion(version);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleChangeStatusClick = () => {
    if (!selectedVersion) return;
    setPendingStatus(selectedVersion.status as VersionStatus);
    handleMenuClose();
    setChangeStatusDialogOpen(true);
  };

  const handleChangeStatusConfirm = async () => {
    if (!selectedVersion) return;
    if (pendingStatus === selectedVersion.status) {
      setChangeStatusDialogOpen(false);
      return;
    }
    try {
      await contentService.setVersionStatus(
        contentId,
        selectedVersion.versionNumber,
        pendingStatus,
        teamId,
      );
      await loadVersions();
      onVersionPublished?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to change version status.');
    } finally {
      setChangeStatusDialogOpen(false);
    }
  };

  const handleMakeActiveClick = () => {
    handleMenuClose();
    setMakeActiveDialogOpen(true);
  };

  const handleMakeActiveConfirm = async () => {
    if (!selectedVersion) return;
    try {
      await contentService.setActiveVersion(contentId, selectedVersion.versionNumber, teamId);
      await loadVersions();
      onVersionPublished?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to make version active.');
    } finally {
      setMakeActiveDialogOpen(false);
    }
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedVersion) return;
    try {
      await contentService.deleteVersion(contentId, selectedVersion.versionNumber, teamId);
      await loadVersions();
      onVersionPublished?.();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete version.');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleVersionCreated = async () => {
    await loadVersions();
    onVersionPublished?.();
  };

  const handleViewVersionClick = (versionNumber: number) => {
    const isCurrent = versionNumber === currentVersionNumber;
    if (isCurrent && !selectedVersionNumber) return;
    onVersionSelect?.(isCurrent ? null : versionNumber);
    onClose();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 420, p: 0, top: '64px', height: 'calc(100% - 64px)' } }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Version History</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canPublish && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={() => setCreateDialogOpen(true)}
              >
                New Version
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : versions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No versions found.
            </Typography>
          ) : (
            <List disablePadding>
              {versions.map((version, index) => {
                const isCurrent = version.versionNumber === currentVersionNumber;
                const isViewing = version.versionNumber === (selectedVersionNumber ?? currentVersionNumber);

                const statusCfg = VERSION_STATUS_CONFIG[version.status] ?? { label: version.status, color: 'default' as const };

                const canChangeStatus = canPublish;
                const canMakeActive = canPublish && !isCurrent;
                const canDeleteThis = canPublish && !isCurrent;
                const dateLabel = version.publishedAt
                  ? new Date(version.publishedAt).toLocaleDateString()
                  : version.createdAt
                    ? new Date(version.createdAt).toLocaleDateString()
                    : null;

                // Title: use live contentTitle for current version, snapshot otherwise
                const displayTitle = isCurrent ? contentTitle : version.titleAtPublish;

                return (
                  <React.Fragment key={version.versionNumber}>
                    {index > 0 && <Divider sx={{ my: 1 }} />}
                    <ListItem
                      disablePadding
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        py: 1,
                        px: 0.5,
                        bgcolor: isViewing ? 'primary.light' : undefined,
                        borderRadius: 1,
                        opacity: isViewing ? 1 : 0.85,
                        '&:hover': { bgcolor: isViewing ? 'primary.light' : 'action.hover' },
                      }}
                    >
                      {/* Version header row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" component="span">
                            v{version.versionNumber}
                          </Typography>
                          {isCurrent && (
                            <Chip label="Current" size="small" color="primary" sx={{ ml: 1, height: 18 }} />
                          )}
                          {isViewing && !isCurrent && (
                            <Chip label="Viewing" size="small" color="secondary" sx={{ ml: 1, height: 18 }} />
                          )}
                          {displayTitle && (
                            <Typography
                              variant="body2"
                              color={isViewing ? 'primary.contrastText' : 'text.secondary'}
                              noWrap
                              sx={{ mt: 0.25 }}
                            >
                              {displayTitle}
                            </Typography>
                          )}
                        </Box>

                        <Chip
                          label={statusCfg.label}
                          size="small"
                          color={statusCfg.color}
                          variant="outlined"
                          sx={{ mr: 0.5, flexShrink: 0 }}
                        />

                        {/* View button — non-drafts always; drafts only for publishers */}
                        {onVersionSelect && (version.status !== 'draft' || canPublish) && (
                          <IconButton
                            size="small"
                            title={isCurrent ? 'Return to current version' : `View v${version.versionNumber}`}
                            onClick={() => handleViewVersionClick(version.versionNumber)}
                            disabled={isViewing && isCurrent}
                          >
                            <ViewVersionIcon fontSize="small" />
                          </IconButton>
                        )}

                        {(canChangeStatus || canMakeActive || canDeleteThis) && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, version)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Analytics row */}
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ViewCountIcon fontSize="small" color="action" sx={{ fontSize: 14 }} />
                          <Typography variant="caption">{version.viewCount}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DownloadIcon fontSize="small" color="action" sx={{ fontSize: 14 }} />
                          <Typography variant="caption">{version.downloadCount}</Typography>
                        </Box>
                        {dateLabel && (
                          <Typography variant="caption" color={isViewing ? 'primary.contrastText' : 'text.secondary'}>
                            {dateLabel}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Version action menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {canPublish && (
          <MenuItem onClick={handleChangeStatusClick}>
            <ListItemIcon><ChangeStatusIcon fontSize="small" /></ListItemIcon>
            <Typography variant="inherit">Change Status</Typography>
          </MenuItem>
        )}
        {canPublish && selectedVersion && selectedVersion.versionNumber !== currentVersionNumber && (
          <MenuItem onClick={handleMakeActiveClick}>
            <ListItemIcon><MakeActiveIcon fontSize="small" /></ListItemIcon>
            <Typography variant="inherit">Make Active</Typography>
          </MenuItem>
        )}
        {canPublish && selectedVersion && selectedVersion.versionNumber !== currentVersionNumber && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <Typography variant="inherit">Delete Version</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Change Status dialog */}
      <Dialog open={changeStatusDialogOpen} onClose={() => setChangeStatusDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Status — v{selectedVersion?.versionNumber}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls who can access this version.
            {selectedVersion?.versionNumber === currentVersionNumber && ' As the active version, this also updates the content\'s status.'}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={pendingStatus}
              label="Status"
              onChange={(e) => setPendingStatus(e.target.value as VersionStatus)}
            >
              <MenuItem value="public">Public — visible to all readers</MenuItem>
              <MenuItem value="private">Private — team members only</MenuItem>
              <MenuItem value="draft">Draft — publishers only</MenuItem>
              <MenuItem value="archived">Archived — author &amp; admins only</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeStatusDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleChangeStatusConfirm}
            disabled={pendingStatus === selectedVersion?.status}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Make Active confirmation dialog */}
      <Dialog open={makeActiveDialogOpen} onClose={() => setMakeActiveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Make v{selectedVersion?.versionNumber} Active</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            v{selectedVersion?.versionNumber} will become the active version. Its current status
            ({selectedVersion?.status}) will carry over to the content record. The previous active
            version keeps its own status unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMakeActiveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMakeActiveConfirm}>
            Make Active
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete version confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete v{selectedVersion?.versionNumber}?</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Version {selectedVersion?.versionNumber} will be permanently deleted. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create version dialog */}
      <CreateVersionDialog
        open={createDialogOpen}
        contentId={contentId}
        organizationId={organizationId}
        teamId={teamId}
        currentVersionNumber={currentVersionNumber}
        currentVersionStatus={versions.find(v => v.versionNumber === currentVersionNumber)?.status as any}
        contentMimeType={contentMimeType}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handleVersionCreated}
      />
    </>
  );
};

export default VersionHistoryPanel;
